import { ChangeDetectorRef, Component, OnInit, OnDestroy } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
} from "@angular/forms";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { IPaginationResult, IRequestResult } from "app/common/interfaces";
import { ExportOptions } from "app/layout/export-overlay/tasks/core/export-task.interfaces";
import { WarehouseService } from "app/modules/components/configuration/warehouse/warehouse.service";
import { SubRouteService } from "app/modules/components/routing/sub-routes/sub-route.service";
import { getCode } from "app/utils/method";
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  Subject,
  Subscription,
  skip,
  take,
  takeUntil,
} from "rxjs";
import { ExportDialogComponent } from "app/layout/export-overlay/dialog/export-dialog.component";
import { OrderCustomerService } from "../order-customer/order-customer.service";
import {
  DocumentClassAttributes,
  MaterialAttributes,
  OrderDetailAttributes,
  SubRouteAttributes,
  WarehouseAttributes,
} from "@albatrosdeveloper/ave-models-package";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums/status.enum";
import ReturnTypeAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/masters/return-type/return-type.entity";
import { CommonModule, DecimalPipe } from "@angular/common";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import {
  MatSidenavContainer,
  MatSidenavModule,
} from "@angular/material/sidenav";

// Smart Grid imports
import { SmartGridComponent } from 'app/shared/components/smart-grid/components/smart-grid/smart-grid.component';
import { ColDef } from 'app/shared/components/smart-grid/core/interfaces/col-def.interface';
import { GridConfig } from 'app/shared/components/smart-grid/core/interfaces/grid-config.interface';
import { PageChangedEvent } from 'app/shared/components/smart-grid/core/interfaces/pagination-state.interface';
import { SortChangedEvent } from 'app/shared/components/smart-grid/core/interfaces/sort-state.interface';
import { SearchChangedEvent } from 'app/shared/components/smart-grid/core/interfaces/grid-events.interface';

// Cell Renderers
import { RouteUserCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/route-user-cell-renderer/route-user-cell-renderer.component';
import { CustomerCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/customer-cell-renderer/customer-cell-renderer.component';
import { MaterialCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/material-cell-renderer/material-cell-renderer.component';
import { QuantityCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/quantity-cell-renderer/quantity-cell-renderer.component';
import { DateHourCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/date-hour-cell-renderer/date-hour-cell-renderer.component';
import { DecimalCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/decimal-cell-renderer/decimal-cell-renderer.component';
import { CurrencyCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/currency-cell-renderer/currency-cell-renderer.component';
import { ActionsCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/actions-cell-renderer/actions-cell-renderer.component';
import { SerialNavigationCellRendererComponent } from 'app/modules/components/reports/exchange/request-change/cell-renderers/serial-navigation-cell-renderer.component';
import { ReturnTypeCellRendererComponent } from './cell-renderers/return-type-cell-renderer.component';

import { FormatOtherDateHourPipe } from "app/pipes/other-date.pipe";

import {
  ORDER_RETURN_EXPORT_ID,
  ORDER_RETURN_TITLE,
} from "../order-customer/exports/order-customer.constants";
import { OrderCustomerExport } from "../order-customer/exports/order-customer.export";
import { CompanyService } from "app/modules/components/configuration/company/company.service.service";
import { SearchService } from "app/layout/common/search-page/search-page.service";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { environment } from "environments/environment.prod";
import { OrdersListService } from "app/modules/components/sales/order-list-components/order-list.service";
import { MasterUserInterface } from "app/modules/components/sales/order-generate/interfaces/master-user.interface";
import { MaterialService } from "app/modules/components/material/material/material.service";
import { ChangeConditionService } from "app/modules/components/master/change-condition/change-condition.service";
import { DecimalFormatPipe } from "app/pipes/decimal-company.pipe";
import { DocumentFlowButtonComponent } from "app/modules/shared/document-flow-button/document-flow-button.component";
import { MatMenuModule } from "@angular/material/menu";
import { FilterSectionComponent } from "app/common/components/filter-section/filter-section.component";
import { BusinessPartnersFiltersComponent } from "app/modules/components/filters/business-partners-filters/business-partners-filters.component";
import { ConfigurationFiltersComponent } from "app/modules/components/filters/configuration-filters/configuration-filters.component";
import { DatesFiltersComponent } from "app/modules/components/filters/dates-filters/dates-filters.component";
import { DocumentsFiltersComponent } from "app/modules/components/filters/documents-filters/documents-filters.component";
import { MaterialsManagementFiltersComponent } from "app/modules/components/filters/materials-management-filters/materials-management-filters.component";
import { RoutesFiltersComponent } from "app/modules/components/filters/routes-filters/routes-filters.component";
import { StatusFiltersComponent } from "app/modules/components/filters/status-filters/status-filters.component";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";
import { UserRouteDisplayComponent } from "app/shared/components/user-route-display/user-route-display.component";
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { DateHelper } from "app/utils/date.helper";
import { CompanyCurrencyPipe } from "app/pipes/currency.pipe";
import { DecimalConfigService } from "app/services/decimal-config.service";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { MatTableDataSource } from "@angular/material/table";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { BusinessPartnerCellRendererComponent } from "app/modules/components/monitor/monitor-routes/cell-renderers/business-partner-cell-renderer.component";


@Component({
  selector: "app-return-product",
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavContainer,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    MatDialogModule,
    NgxMatSelectSearchModule,
    FormatOtherDateHourPipe,
    FilterComponent,
    MatAutocompleteModule,
    MatCheckboxModule,
    DecimalFormatPipe,
    DocumentFlowButtonComponent,
    MatMenuModule,
    FilterSectionComponent,
    StatusFiltersComponent,
    MaterialsManagementFiltersComponent,
    DatesFiltersComponent,
    ConfigurationFiltersComponent,
    DocumentsFiltersComponent,
    RoutesFiltersComponent,
    BusinessPartnersFiltersComponent,
    UserRouteDisplayComponent,
    HoverMenuDirective,
    CompanyCurrencyPipe,
    SmartGridComponent,
  ],
  providers: [OrderCustomerExport, DecimalPipe],
  templateUrl: "./return-product-request.component.html",
  styleUrl: "./return-product-request.component.scss",
})
export class ReturnProductRequestComponent implements OnInit, OnDestroy {
  private isInitialized = false;
  getCode = getCode;
  private decimalPipe = new DecimalPipe("en-US");
  StatusEnum = StatusEnum;
  type = DocumentTypeEnum.SOLICITUD_DEVOLUCION_PRODUCTO; // SOLICITUD DEVOLUCIÓN DE PRODUCTO
  displayedColumns: string[] = [
    "routeUser",
    "serials",
    "date",
    "returnType",
    "shipToExternalKeyCode",
    "materialName",
    "soldQuantity",
    "unitPrice",
    "subTotal",
    "total",
    "actions",
  ];

  dataSource = new MatTableDataSource<OrderDetailAttributes>([]);

  // Smart Grid data
  data: any[] = [];
  totalItems: number = 0;
  loading: boolean = false;
  searchControl = new FormControl("");
  search: string = "";
  pageSize: number = 50;
  pageIndex: number = 1;
  sortActive: keyof OrderDetailAttributes = "total";
  sortDirection: "ASC" | "DESC" = "DESC";

  filter = {
    dateini: new Date(),
    dateend: new Date(),
    warehouseId: null,
    item: null,
    documentType: null,
    userId: null,
    soldTo: null,
    shipTo: null,
    returnType: null,
    state: null,
    routes: [],
    company: null,
    status: null,
  };

  range = new FormGroup({
    start: new FormControl<Date | null>(new Date()),
    end: new FormControl<Date | null>(new Date()),
  });

  warehouseList: WarehouseAttributes[] = [];
  warehouseFilterList: WarehouseAttributes[] = [];
  searchWarehouse = "";

  subRouteList: SubRouteAttributes[] = [];
  listFilterSubRouteList: SubRouteAttributes[] = [];
  searchSubRoute = "";

  UMPActive: boolean = false;
  currencyCode: string = "";

  searchSubscription: Subscription;

  selectedSoldTo: string = "0";
  soldToControl = new FormControl("");
  soldToList: any[] = [];
  soldToFilterSelected: any = null;

  selectedShipTo: string = "0";
  shipToControl = new FormControl("");
  shipToList: any[] = [];

  sellerList: MasterUserInterface[] = [];
  sellerFilterList: MasterUserInterface[] = [];
  searchSeller = "";

  documentClassesList: DocumentClassAttributes[] = [];
  filteredDocumentClasses: Subject<DocumentClassAttributes[]> = new Subject();
  selectSeller: string = "0";

  searchMaterial = "";
  searchReturnType = "";
  materialList: MaterialAttributes[] = [];
  returnTypeList: ReturnTypeAttributes[] = [];
  private searchSubject = new Subject<string>();

  orderStatusErpList = [
    { id: null, name: "Todos" },
    { id: 0, name: "Devolución pendiente" },
    { id: 4, name: "Error enviando a ERP" },
    { id: 1, name: "Devolución enviado a ERP" },
    { id: 2, name: "Devolución recibido por ERP" },
    { id: 3, name: "Devolución creado en ERP" },
  ];
  selectedStatusERP: number[] = [];
  isStatusErpIndeterminate = false;
  isStatusErpChecked = false;
  statusFilterCtrlErp = new FormControl();
  filteredStatusERP: BehaviorSubject<any[]> = new BehaviorSubject([]);
  private _onDestroy = new Subject<void>();

  orderStatusList = [
    { id: null, name: "Todos" },
    { id: StatusEnum.INACTIVE, name: "Anulado" }, // 0
    { id: StatusEnum.ACTIVE, name: "Creado" }, // 1
    { id: StatusEnum.ORDER_IN_PROGRESS, name: "En proceso de aprobación" }, // 6
    { id: StatusEnum.ORDER_APPROVED, name: "Aprobado" }, // 7
    { id: StatusEnum.ORDER_REJECTED, name: "Rechazado" }, // 8
    { id: StatusEnum.ORDER_DELIVERED, name: "Entregado por cliente" }, // 9
    {
      id: StatusEnum.ORDER_PARTIAL_DELIVERED,
      name: "Entregado parcialmente por el cliente",
    }, // 10
    {
      id: StatusEnum.ORDER_DELIVERY_REJECTED,
      name: "No entregado por cliente",
    }, // 11
    { id: StatusEnum.ORDER_PARTIAL_APPROVED, name: "Aprobado parcialmente" }, // 14
  ];
  selectedStatus: number[] = [];
  isStatusIndeterminate = false;
  isStatusChecked = false;
  filteredStatus: BehaviorSubject<any[]> = new BehaviorSubject([]);
  statusFilterCtrl = new FormControl();

  form: FormGroup;

  //Para filtros
  selectedStatuses: number[] = [];

  // Estados ERP
  selectedERPStatuses: number[] = [];

  selectedCustomerType: string[] = [];

  // Smart Grid Configuration
  columnDefs: ColDef<any>[] = [
    {
      colId: "routeUser",
      field: "subRoute.name",
      pinned: "left",
      headerName: "Ruta - Usuario",
      cellRenderer: RouteUserCellRendererComponent,
      minWidth: 300,
      sortable: true,
      filterOptions: {
        valueExtractor: (item: any) => {
          const subRoute = item.subRoute;
          const user = item.user;
          const routeName = subRoute?.name ?? "-";
          const userName = user?.name ?? "-";
          return `${routeName} - ${userName}`;
        },
        customFilterFn: (rowData: any, filterValue: string) => {
          const subRoute = rowData.subRoute;
          const user = rowData.user;
          const routeName = subRoute?.name ?? "-";
          const userName = user?.name ?? "-";
          const displayValue = `${routeName} - ${userName}`;
          if (!displayValue || displayValue === "- - -") return !filterValue;
          return displayValue === filterValue;
        },
      },
    },
    {
      field: "serials",
      headerName: "Nro. Doc.",
      cellRenderer: SerialNavigationCellRendererComponent,
      cellRendererParams: {
        documentType: this.type, // DT010
      },
      minWidth: 160,
      sortable: true,
    },
    {
      field: "date",
      headerName: "Fecha",
      cellRenderer: DateHourCellRendererComponent,
      minWidth: 180,
      sortable: true,
      filterOptions: {
        valueExtractor: (item: any) => {
          if (!item.date) return "-";
          const date = new Date(item.date);
          return date.toLocaleDateString("es-ES");
        },
        customFilterFn: (rowData: any, filterValue: string) => {
          if (!rowData.date) return !filterValue;
          const date = new Date(rowData.date);
          const formattedDate = date.toLocaleDateString("es-ES");
          return formattedDate === filterValue;
        },
      },
    },
    {
      field: "returnType",
      headerName: "Motivo de Devolución",
      cellRenderer: ReturnTypeCellRendererComponent,
      minWidth: 200,
      sortable: false,
    },
    {
      field: "shipToExternalKeyCode",
      headerName: "Pagador/Receptor",
      cellRenderer: BusinessPartnerCellRendererComponent,
      autoSize: true,
      minWidth: 250, // Much wider for code + long name
      sortable: true,
      filterOptions: {
        // Display customer name in filter dropdown instead of code
        // Note: Requires shipToName field to be present in row data
        valueExtractor: (item: any) => item?.soldTo?.name,
        customFilterFn(rowData, filterValue) {
          const rowCustomer = rowData?.soldTo?.name;
          if (!rowCustomer) return !filterValue;

          return rowCustomer === filterValue;
        },
      },
    },
    {
      field: "materialName",
      headerName: "Material",
      cellRenderer: MaterialCellRendererComponent,
      minWidth: 300,
      sortable: true,
      filterOptions: {
        valueExtractor: (item: any) => item.materialName?.name ?? "-",
        customFilterFn: (rowData: any, filterValue: string) => {
          const materialName = rowData.materialName?.name ?? "-";
          if (!materialName || materialName === "-") return !filterValue;
          return materialName === filterValue;
        },
      },
    },
    {
      field: "soldQuantity",
      headerName: "Cantidad",
      cellRenderer: QuantityCellRendererComponent,
      cellRendererParams: {
        getUMPActive: () => {
          const result = this.UMPActive;
          return result;
        },
      },
      minWidth: 180,
      sortable: true,
    },
    {
      field: "unitPrice",
      headerName: "Precio",
      cellRenderer: CurrencyCellRendererComponent,
      minWidth: 120,
      sortable: true,
      cellClass: "text-right",
      filterOptions: this.createCurrencyFilterOptions("unitPrice"),
    },
    {
      field: "subTotal",
      headerName: "Subtotal",
      cellRenderer: CurrencyCellRendererComponent,
      minWidth: 120,
      sortable: true,
      cellClass: "text-right",
      filterOptions: this.createCurrencyFilterOptions("subTotal"),
    },
    {
      field: "total",
      headerName: "Total",
      cellRenderer: CurrencyCellRendererComponent,
      minWidth: 120,
      sortable: true,
      cellClass: "text-right",
      filterOptions: this.createCurrencyFilterOptions("total"),
    },
    {
      colId: "actions",
      headerName: "Acciones",
      cellRenderer: ActionsCellRendererComponent,
      cellClass: "text-center",
      headerClass: "text-center",
      minWidth: 80,
      width: 80,
      sortable: false,
      pinned: "right",
    },
  ];

  gridConfig: GridConfig<any> = {
    rowIdField: "_id" as any,
    emptyState: {
      message: "No encontramos tus resultados!",
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: "api",
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: "api",
      multiColumn: false,
      defaultSort: [{ field: "total" as any, direction: "desc" }],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: "local",
    },
    columnVisibility: {
      enabled: false, // Disabled - now in settings panel
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40, // Increased for more spacing
    },
    styling: {
      maxHeight: "85vh",
      minHeight: "80vh",
      density: "comfortable",
      fontSize: "12px",
      cellPaddingX: "3px",
      cellPaddingY: "3px",
    },
    settingsPanel: {
      enabled: true,
      position: "right",
      enableColumnVisibility: true,
      defaultDensity: "compact",
    },
  };

  loadConfigData = false;
  loadDocumentsData = false;
  loadRoutesData = false;
  loadBusinessPartnersData = false;
  loadMaterialsData = false;

  constructor(
    private orderCustomer: OrderCustomerService,
    private readonly warehouseService: WarehouseService,
    private readonly subRouteService: SubRouteService,
    private readonly companyService: CompanyService,
    private readonly searchService: SearchService,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    private orderCustomerExport: OrderCustomerExport,
    private ordersListService: OrdersListService,
    private readonly cdr: ChangeDetectorRef,
    private readonly materialService: MaterialService,
    private changeConditionService: ChangeConditionService,
    private companyCurrencyPipe: CompanyCurrencyPipe,
    private decimalConfigService: DecimalConfigService,
    private fb: FormBuilder
  ) {
    console.log("🏗️ [Constructor] UMPActive inicial:", this.UMPActive);
    const today = new Date();
    this.form = this.fb.group({
      start: [today],
      end: [today],
      startTrip: [],
      endTrip: [],
      companyId: [],
      salesOfficeId: [],
      documentClassId: [],
      subrouteId: [],
      assignedUserId: [],
      payingBusinessPartnerId: [],
      receivingBusinessPartnerId: [],
      warehouseId: [],
      end_startTrip: [],
      end_endTrip: [],
      soldToControl: [],
      searchReceivingBusinessPartner: [],
      searchAssignedUser: [],
      materialGroupId: [[]],
      materialId: [],
      returnType: null,
    });
  }

  private formatCurrency(value: number): string {
    if (value == null) return "-";
    const decimals = this.decimalConfigService.getDecimals();
    const formattedNum =
      this.decimalPipe.transform(value, `1.${decimals}-${decimals}`) ?? "-";
    if (formattedNum === "-") return "-";
    const symbol = this.companyCurrencyPipe.transform("symbol");
    return `${symbol} ${formattedNum}`;
  }

  private createCurrencyFilterOptions(fieldPath: string) {
    return {
      valueExtractor: (item: any) => {
        const value = item[fieldPath];
        return this.formatCurrency(value);
      },
      customFilterFn: (rowData: any, filterValue: string) => {
        const value = rowData[fieldPath];
        const formattedValue = this.formatCurrency(value);
        if (!formattedValue || formattedValue === "-") return !filterValue;
        return formattedValue === filterValue;
      },
    };
  }

  onConfigExpanded(): void {
    this.loadConfigData = true;
  }

  onDocumentsExpanded(): void {
    this.loadDocumentsData = true;
  }

  onRoutesExpanded(): void {
    this.loadRoutesData = true;
  }

  onBusinessPartnersExpanded(): void {
    this.loadBusinessPartnersData = true;
  }

  onMaterialsExpanded(): void {
    this.loadMaterialsData = true;
  }

  ngOnInit(): void {
    console.log("🚀 [ngOnInit] START - UMPActive:", this.UMPActive);

    this.onHandleSearch();
    console.log(
      "🚀 [ngOnInit] Después de onHandleSearch() - UMPActive:",
      this.UMPActive
    );

    this.fetchCompanies(); // ⚠️ Async - no espera
    console.log(
      "🚀 [ngOnInit] Después de fetchCompanies() call - UMPActive:",
      this.UMPActive
    );

    this.onFilter();
    console.log(
      "🚀 [ngOnInit] Después de onFilter() - UMPActive:",
      this.UMPActive
    );

    this.isInitialized = true;
    console.log("🚀 [ngOnInit] END - UMPActive:", this.UMPActive);
  }

  ngAfterViewInit(): void {
    this.searchControl.valueChanges
      .pipe(
        skip(1),
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this._onDestroy)
      )
      .subscribe((filterValue: string) => {
        if (this.isInitialized) {
          this.pageIndex = 1;
          this.search = filterValue;
          this.onFilter();
        }
      });

    this.setupMaterialSearchControl();
    this.getMaterialList();

    this.soldToControl.valueChanges
      .pipe(
        skip(1),
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this._onDestroy)
      )
      .subscribe((value) => {
        if (this.isInitialized) {
          if (
            typeof value === "string" &&
            value.trim().length === 24 &&
            /^[a-f0-9]{24}$/.test(value)
          ) {
            return;
          }
          this.soldToList = [];
          this.soldToFilterSelected = null;
          this.getListSoldTo(value);

          this.getListReport();
        }
      });

    this.shipToControl.valueChanges
      .pipe(
        skip(1),
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this._onDestroy)
      )
      .subscribe((value) => {
        if (this.isInitialized) {
          if (
            typeof value === "string" &&
            value.trim().length === 24 &&
            /^[a-f0-9]{24}$/.test(value)
          ) {
            return;
          }

          this.shipToList = [];
          this.getListShipTo(value);
          this.getListReport();
        }
      });

    this.filteredStatusERP.next(this.orderStatusErpList);
    this.statusFilterCtrlErp.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterStatusERP();
      });

    this.filteredStatus.next(this.orderStatusList);
    this.statusFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterStatus();
      });
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$
      .pipe(skip(1), debounceTime(300))
      .subscribe((term) => {
        if (this.isInitialized) {
          this.pageIndex = 1;
          this.search = term;
          this.onFilter();
        }
      });
  }

  onFilter() {
    console.log("🔍 [onFilter] INICIO - UMPActive:", this.UMPActive);

    this.range.valueChanges.subscribe((rangeValues) => {
      this.filter.dateini = rangeValues.start || this.filter.dateini;
      this.filter.dateend = rangeValues.end || this.filter.dateend;
    });

    this.getListReport();
  }

  onClearFilter() {
    this.filter = {
      dateini: new Date(),
      dateend: new Date(),
      warehouseId: null,
      item: null,
      documentType: null,
      userId: null,
      soldTo: null,
      shipTo: null,
      returnType: null,
      state: null,
      routes: [],
      company: null,
      status: null,
    };
    this.range.reset({
      start: new Date(),
      end: new Date(),
    });
    const today = new Date();
    this.form.reset({ start: today, end: today });
    this.onFilter();
    this.selectSeller = "0";
  }

  applyFilter(filterValue: string) {
    this.pageIndex = 1;
    this.search = filterValue;
    this.getListReport();
  }

  changeSort(e) {
    this.sortActive = e.active;
    this.sortDirection = e.direction?.toUpperCase() || "ASC";
    this.getListReport();
  }

  // SmartGrid Event Handlers
  onPageChanged(event: PageChangedEvent): void {
    this.pageIndex = event.pageIndex + 1; // Convert 0-based to 1-based
    this.pageSize = event.pageSize;
    this.getListReport();
  }

  onSortChanged(event: SortChangedEvent<any>): void {
    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0];
      this.sortActive = sortState.field as keyof OrderDetailAttributes;
      this.sortDirection = sortState.direction === "asc" ? "ASC" : "DESC";
    }
    this.getListReport();
  }

  onSearchChanged(event: SearchChangedEvent): void {
    this.search = event.searchTerm;
    this.pageIndex = 1;
    this.getListReport();
  }

  // Legacy pagination handler (kept for compatibility if needed)
  changePage(e) {
    this.pageIndex = e.pageIndex + 1;
    this.pageSize = e.pageSize;
    this.getListReport();
  }

  getListReport() {
    console.log("📋 [getListReport] INICIO - UMPActive:", this.UMPActive);
    this.loading = true;

    let options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.search,
      sort: this.getSortField(),
      sortDirection: this.sortDirection,
    };

    const adjustedStart = new Date(this.filter.dateini);
    adjustedStart.setHours(0, 0, 0, 0); // 00:00:00.000

    const adjustedEnd = new Date(this.filter.dateend);
    adjustedEnd.setHours(23, 59, 59, 999); // 23:59:59.999

    // returnType: this.filter.returnType,
    let filter: IFilters = {
      type: this.type,
      startDate: DateHelper.getStartOfDay(this.form.get("start")?.value),
      endDate:
        DateHelper.getEndOfDay(this.form.get("end")?.value) ??
        DateHelper.getEndOfDay(this.form.get("start")?.value) ??
        null,
      startTrip: DateHelper.getStartOfDay(this.form.get("startTrip")?.value),
      endTrip:
        DateHelper.getEndOfDay(this.form.get("endTrip")?.value) ??
        DateHelper.getEndOfDay(this.form.get("startTrip")?.value) ??
        null,
      end_startTrip: DateHelper.getStartOfDay(
        this.form.get("end_startTrip")?.value
      ),
      end_endTrip:
        DateHelper.getEndOfDay(this.form.get("end_endTrip")?.value) ??
        DateHelper.getEndOfDay(this.form.get("end_startTrip")?.value) ??
        null,
      companyId: this.form.get("companyId")?.value,
      salesOfficeId: this.form.get("salesOfficeId")?.value,
      warehouseId: this.form.get("warehouseId")?.value, //
      documentClass: this.form.get("documentClassId")?.value,
      subRouteId: this.form.get("subRouteId")?.value, //
      soldTo: this.form.get("payingBusinessPartnerId")?.value,
      shipTo: this.form.get("receivingBusinessPartnerId")?.value,
      seller: this.form.get("assignedUserId")?.value,
      status: this.selectedStatuses ? this.selectedStatuses : null,
      statusERP: this.selectedERPStatuses.length
        ? this.selectedERPStatuses
        : null,
      typeCustomer: this.selectedCustomerType.length
        ? this.selectedCustomerType
        : null,
      materialGroupId: this.form.get("materialGroupId").value,
      materialId: this.form.get("materialId").value,
      returnType: this.filter.returnType,
      ordersToFilter: null,
    };
    this.orderCustomer.findAllWithCount(options, filter).subscribe({
      next: (res: IPaginationResult<OrderDetailAttributes>) => {
        this.data = res?.data?.[0]?.data ?? [];
        this.totalItems = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.loading = false;
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al obtener reporte",
          "Cerrar",
          { duration: 5000 }
        );
        this.loading = false;
      },
    });
  }

  private getSortField(): keyof OrderDetailAttributes {
    const sortFieldMapping: Record<string, keyof OrderDetailAttributes> = {
      "subRoute.name": <any>"subRoute.name",
      subRoute: <any>"subRoute.name",
      user: <any>"user",
      serials: <any>"serials",
      date: <any>"date",
      shipToExternalKeyCode: <any>"shipToExternalKeyCode",
      materialName: <any>"materialName.name",
      soldQuantity: "soldQuantity",
      baseUnitOfMeasure: "baseUnitOfMeasure",
      unitPrice: "unitPrice",
      subTotal: "subTotal",
      total: "total",
    };
    return sortFieldMapping[this.sortActive] || "total";
  }

  //filter
  private fetchCompanies(): void {
    console.log(
      "🏢 [fetchCompanies] START - UMPActive actual:",
      this.UMPActive
    );

    this.companyService.findToComboBox().subscribe((res) => {
      console.log("🏢 [fetchCompanies] ✅ Observable FIRED");
      console.log("🏢 [fetchCompanies] Response completo:", res);
      console.log("🏢 [fetchCompanies] res.data.length:", res.data.length);

      if (res.data.length === 1) {
        console.log("🏢 [fetchCompanies] Compañía encontrada:", res.data[0]);
        console.log(
          "🏢 [fetchCompanies] res.data[0].UMPActive:",
          res.data[0].UMPActive
        );
        console.log("🏢 [fetchCompanies] Tipo:", typeof res.data[0].UMPActive);

        this.UMPActive = res.data[0].UMPActive ?? false;
        this.currencyCode = res.data[0].currencyBase?.code || "";
        this.updatePriceHeaders();

        console.log(
          "🏢 [fetchCompanies] ✅ UMPActive ASIGNADO:",
          this.UMPActive
        );
      } else {
        console.log(
          "🏢 [fetchCompanies] ⚠️ No exactamente 1 compañía, setting false"
        );
        this.UMPActive = false;
        this.currencyCode = "";
      }

      console.log("🏢 [fetchCompanies] END - UMPActive final:", this.UMPActive);
    });
  }

  /**
   * Update price column headers with currency code
   * NOTE: Not needed anymore since CurrencyCellRendererComponent shows symbol in cells
   */
  private updatePriceHeaders(): void {
    // Currency symbols are now displayed in cells via CurrencyCellRendererComponent
    // Headers remain simple: "Precio", "Subtotal", "Total"
    // No need to update headers dynamically
  }

  private fetchAllWarehouses(): void {
    this.warehouseService.findToComboBox().subscribe({
      next: (res) => {
        this.warehouseList = res.data;
        this.warehouseFilterList = this.warehouseList.slice();
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error fetching warehouses",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  changueOptionsWarehouse(event) {
    if (!this.warehouseList) {
      return;
    }
    let search = event;
    if (!search) {
      this.warehouseFilterList = this.warehouseList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.warehouseFilterList = this.warehouseList.filter(
      (item) =>
        item.externalKeys?.code.toLowerCase().indexOf(search) > -1 ||
        item.name.toLowerCase().indexOf(search) > -1
    );
  }

  changueOptionsSeller(event) {
    if (!this.sellerList) {
      return;
    }
    let search = event;
    if (!search) {
      this.sellerFilterList = this.sellerList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.sellerFilterList = this.sellerList.filter(
      (item) =>
        item.masterUserId.toString().toLowerCase().indexOf(search) > -1 ||
        item.username.toString().toLowerCase().indexOf(search) > -1
    );
  }

  changeWarehouse(event): void {
    this.subRouteList = [];
    this.listFilterSubRouteList = [];
    this.getAllRutas();
  }

  private getAllRutas(): void {
    this.subRouteService
      .findToWarehouse(this.filter.warehouseId ? [this.filter.warehouseId] : [])
      .subscribe({
        next: (res) => {
          this.subRouteList = res.data;
          this.listFilterSubRouteList = this.subRouteList.slice();
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error fetching route",
            "Cerrar",
            { duration: 5000 }
          );
        },
      });
  }

  changueOptionsSubRoute(event) {
    if (!this.subRouteList) {
      return;
    }
    let search = event;
    if (!search) {
      this.listFilterSubRouteList = this.subRouteList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.listFilterSubRouteList = this.subRouteList.filter(
      (item) =>
        item.externalKeys?.code.toLowerCase().indexOf(search) > -1 ||
        item.name.toLowerCase().indexOf(search) > -1
    );
  }

  fetchSoldToName = (soldToId: string): string => {
    const foundSoldTo = this.soldToList.find(
      (soldTo) => soldTo.id === soldToId
    );
    if (foundSoldTo) {
      return foundSoldTo.name;
    }
    return "";
  };

  fetchShipToName = (shipToId: string): string => {
    const foundShipTo = this.shipToList.find(
      (shipTo) => shipTo.id === shipToId
    );
    if (foundShipTo) {
      return foundShipTo.name;
    }
    return "";
  };

  onChangeSoldTo() {
    this.soldToFilterSelected = this.soldToList.find(
      (stl) => stl.id == this.soldToControl.value
    );
    this.shipToControl.setValue("");
    this.getListShipTo();
  }

  getListShipTo(search?): void {
    this.shipToList = [];
    const soldToValue = this.soldToFilterSelected
      ? this.soldToFilterSelected._id
      : null;

    let dataToSend: any = {
      companyIds: [environment.companyId],
      search: search?.toString() || "",
    };

    if (soldToValue) {
      dataToSend.soldToMongoId = soldToValue;
    }

    this.ordersListService.fetchShipTos(dataToSend).subscribe((res) => {
      this.shipToList = res?.data;
      this.cdr.detectChanges();
    });
  }

  changueOptionsMaterial(event) {
    this.searchSubject.next(event);
  }

  onExportButtonPressed() {
    if (!this.orderCustomerExport.canStartTask()) return;
    const dialogRef = this.matDialog.open(ExportDialogComponent, {
      data: { title: ORDER_RETURN_TITLE },
    });
    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.orderCustomerExport.export(
          options.format,
          options.range,
          this.totalItems,
          this.pageSize,
          this.pageIndex,
          ORDER_RETURN_EXPORT_ID,
          ORDER_RETURN_TITLE
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  private getListSeller() {
    this.ordersListService.fetchMasterUsers().subscribe({
      next: (res: IRequestResult<MasterUserInterface[]>) => {
        if (res.status == "success") {
          this.sellerList = res.data;
          this.sellerFilterList = this.sellerList.slice();
        }
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error fetching sellers",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  getListSoldTo(search?): void {
    let dataToSend = {
      companyIds: [environment.companyId],
      search: search?.toString() || "",
    };
    this.ordersListService.fetchSoldTos(dataToSend).subscribe((res) => {
      this.soldToList = res?.data;
      this.cdr.detectChanges();
    });
  }

  private fetchDocumentClass(): void {
    let dataToFilter = {
      type: DocumentTypeEnum.FACTURA,
    };
    this.ordersListService
      .fetchDocumentClasses(dataToFilter)
      .subscribe((res) => {
        this.documentClassesList = res.data;
        this.filteredDocumentClasses.next(this.documentClassesList);
      });
  }

  private setupMaterialSearchControl(): void {
    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchTerm) => {
        if (searchTerm) {
          this.searchMaterial = searchTerm;
          this.getMaterialList();
        }
      });
  }

  private getMaterialList(): void {
    const materialOptions = {
      search: this.searchMaterial ? this.searchMaterial : null,
      currentPage: 1,
      limitPerPage: 50,
      sortActive: "name",
    };

    this.materialService
      .findAllWithCount(
        materialOptions,
        this.filter.company ? this.filter.company : []
      )
      .subscribe({
        next: (res: IPaginationResult<MaterialAttributes>) => {
          this.materialList = res?.data?.[0]?.data ?? [];
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al traer registros",
            "Cerrar",
            { duration: 3000 }
          );
        },
      });
  }

  private getTypeReturnList(): void {
    this.changeConditionService.findToComboBox("DE").subscribe({
      next: (res: IRequestResult<ReturnTypeAttributes[]>) => {
        this.returnTypeList = res?.data ?? [];
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al traer registros",
          "Cerrar",
          { duration: 3000 }
        );
      },
    });
  }

  onStatusSelectionErpChange(selected: number[]): void {
    this.selectedStatusERP = selected;
    this.updateStatusErpSelectionState();
  }

  updateStatusErpSelectionState(): void {
    const total = this.orderStatusErpList.length;
    const selected = this.selectedStatusERP.length;
    this.isStatusErpChecked = total > 0 && selected === total;
    this.isStatusErpIndeterminate = selected > 0 && selected < total;
  }

  toggleSelectAllStatusesErp(isChecked: boolean): void {
    this.selectedStatusERP = isChecked
      ? this.orderStatusErpList.map((os) => os.id)
      : [];
    this.updateStatusErpSelectionState();
  }

  filterStatusERP(): void {
    const search = this.statusFilterCtrlErp.value?.toLowerCase() || "";
    const filtered = this.orderStatusErpList.filter((os) =>
      os.name.toLowerCase().includes(search)
    );
    this.filteredStatusERP.next(filtered);
  }

  onStatusSelectionChange(selected: number[]): void {
    this.selectedStatus = selected;
    this.updateStatusSelectionState();
  }

  updateStatusSelectionState(): void {
    const total = this.orderStatusList.length;
    const selected = this.selectedStatus.length;
    this.isStatusChecked = total > 0 && selected === total;
    this.isStatusIndeterminate = selected > 0 && selected < total;
  }

  filterStatus(): void {
    const search = this.statusFilterCtrl.value?.toLowerCase() || "";
    const filtered = this.orderStatusList.filter((os) =>
      os.name.toLowerCase().includes(search)
    );
    this.filteredStatus.next(filtered);
  }

  toggleSelectAllStatuses(isChecked: boolean): void {
    this.selectedStatusERP = isChecked
      ? this.orderStatusErpList.map((os) => os.id)
      : [];
    this.updateStatusErpSelectionState();
  }
}
